import React, { useState, useEffect } from 'react';
import * as api from '../../api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Spinner from '../../Plateforme/Atoms/Spinner';
import Section from '../Atoms/Section';
import H0 from '../Atoms/H0';
import BlueButton from '../Atoms/BlueButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUrlWithRedirect } from '../Utils/redirectUtils';
import { supabase } from '../../lib/supabaseClient';

const CenteredContainer = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
`;

const PageTitle = styled.div`
    text-align: center;
    margin-bottom: 10px;
`;

const Deadline = styled.div`
    text-align: center;
    color: #dc3545;
    font-weight: 500;
    font-size: 18px;
    margin-bottom: 30px;
`;

const BackLink = styled.div`
    margin-bottom: 20px;

    a {
        color: #007bff;
        text-decoration: none;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const ReglementSection = styled.div`
    background-color: #f8f9fa;
    border: 2px solid #000;
    border-radius: 0px;
    padding: 30px;
    margin: 30px 0;
`;

const ReglementText = styled.p`
    margin-bottom: 20px;
    line-height: 1.6;
    color: #555;
`;

const ReglementActions = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 15px;
    align-items: center;
    margin-top: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const PDFPreview = styled.iframe`
    width: 100%;
    height: 600px;
    border: 1px solid #ddd;
    border-radius: 5px;
    margin: 20px 0;
`;

const UploadLabel = styled.label`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    background-color: #28a745;
    color: white;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
    border: none;
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
    vertical-align: middle;
    margin: 0;

    &:hover {
        background-color: #218838;
    }

    input[type="file"] {
        display: none;
    }
`;

const UploadStatus = styled.div`
    padding: 15px;
    border-radius: 5px;
    margin-top: 15px;

    &.success {
        background-color: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
    }

    &.error {
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
    }
`;

const DocumentLink = styled.a`
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #0056b3;
    }
`;

const DocumentsASignerFD100 = (props) => {
    const { i18n } = useTranslation('common');
    const [loading, setLoading] = useState(true);
    const [participation, setParticipation] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [showPDFPreview, setShowPDFPreview] = useState(false);
    const navigate = useNavigate();

    // FD100 2025 event ID
    const eventId = '67590da5499bcc995c210b40';
    const reglementPath = '/documents/Criteres_Règlement_Le_FD100_2026.pdf';

    const location = useLocation();

    useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) {
            navigate(createUrlWithRedirect('/login', location.pathname));
            return;
        }

        fetchParticipation();
    }, [props.fullUserLoading, props.fullUser, navigate]);

    const fetchParticipation = async () => {
        try {
            setLoading(true);

            // Get user participations
            const userParticipations = await api.getUserParticipations({ userid: props.fullUser.user._id });

            // Find the participation for FD100 2025
            const eventParticipation = userParticipations.data.find(p => p.eventId === eventId);
            setParticipation(eventParticipation);
        } catch (error) {
            console.error("Error fetching participation:", error);
        } finally {
            setLoading(false);
        }
    };

    const trackReglementUpload = async (filePath) => {
        try {
            // Get user's winning projects from participation data
            const projectIds = participation?.data?.projets || [];

            // For each project in the participation, track the upload
            for (const projectId of projectIds) {
                await supabase
                    .from('fd100_laureats_tracking')
                    .upsert({
                        user_id: props.fullUser?.user?._id,
                        project_id: typeof projectId === 'object' ? projectId._id : projectId,
                        event_id: eventId,
                        user_email: props.fullUser?.hsProfile?.email || props.fullUser?.user?.email,
                        user_name: `${props.fullUser?.hsProfile?.firstname || ''} ${props.fullUser?.hsProfile?.lastname || ''}`.trim(),
                        company_name: props.fullUser?.hsCompany?.properties?.name || '',
                        reglement_uploaded: true,
                        reglement_uploaded_at: new Date().toISOString(),
                        reglement_file_path: filePath,
                        updated_at: new Date().toISOString()
                    }, {
                        onConflict: 'user_id,project_id,event_id'
                    });
            }
        } catch (error) {
            console.error('Error tracking règlement upload:', error);
        }
    };

    const handleReglementUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            setUploadStatus({
                type: 'error',
                message: i18n.language === 'fr'
                    ? 'Veuillez uploader un fichier PDF.'
                    : 'Please upload a PDF file.'
            });
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setUploadStatus({
                type: 'error',
                message: i18n.language === 'fr'
                    ? 'Le fichier est trop volumineux (max 10MB).'
                    : 'File is too large (max 10MB).'
            });
            return;
        }

        try {
            setUploadingDocument(true);
            setUploadStatus(null);

            // Upload to Supabase Storage
            const userId = props.fullUser?.user?._id;
            const timestamp = Date.now();
            const fileName = `${userId}_${timestamp}_reglement.pdf`;
            const filePath = `fd100-reglements/${fileName}`;

            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('fd100-documents')
                .upload(filePath, file, {
                    contentType: 'application/pdf',
                    upsert: true
                });

            if (uploadError) {
                throw uploadError;
            }

            // Get public URL
            const { data: urlData } = supabase.storage
                .from('fd100-documents')
                .getPublicUrl(filePath);

            const publicUrl = urlData?.publicUrl;

            // Also upload via API for backward compatibility
            const formData = new FormData();
            formData.append('confidentiality_agreement', file);
            formData.append('participation_id', participation._id);
            formData.append('event_id', eventId);

            await api.uploadConfidentialityAgreement(formData);

            setUploadStatus({
                type: 'success',
                message: i18n.language === 'fr'
                    ? 'Règlement signé uploadé avec succès !'
                    : 'Signed règlement uploaded successfully!'
            });

            // Track the upload in Supabase for all user's winning projects with the storage URL
            await trackReglementUpload(publicUrl || filePath);

            // Refresh data to show uploaded document
            fetchParticipation();
        } catch (error) {
            console.error('Error uploading règlement:', error);
            setUploadStatus({
                type: 'error',
                message: i18n.language === 'fr'
                    ? 'Erreur lors de l\'upload. Veuillez réessayer.'
                    : 'Upload error. Please try again.'
            });
        } finally {
            setUploadingDocument(false);
        }
    };

    if (loading) {
        return (
            <Section className="padding">
                <br/><br/>
                <Spinner />
            </Section>
        );
    }

    return (
        <Section className="padding">
            <CenteredContainer>
                <BackLink>
                    <a onClick={() => navigate('/mywinningprojects')}>
                        &larr; {i18n.language === 'fr' ? 'Retour aux projets lauréats' : 'Back to winning projects'}
                    </a>
                </BackLink>

                <PageTitle>
                    <H0>{i18n.language === 'fr' ? 'Règlement à signer' : 'Rules to sign'}</H0>
                </PageTitle>
                <Deadline>
                    {i18n.language === 'fr' ? '- avant le 09.12.25' : '- before 09.12.25'}
                </Deadline>

                <ReglementSection>
                    <ReglementText>
                        {i18n.language === 'fr' ? (
                            <>
                                <strong>Pour valider votre participation à la sélection des lauréats du FD100, vous devez signer le règlement officiel.</strong>
                                <br /><br />
                                Veuillez suivre les étapes suivantes :
                                <ol>
                                    <li>Téléchargez le règlement en cliquant sur le bouton ci-dessous</li>
                                    <li>Lisez attentivement le document</li>
                                    <li>Signez le document (signature manuscrite ou numérique)</li>
                                    <li>Uploadez le document signé via le bouton d'upload</li>
                                </ol>
                            </>
                        ) : (
                            <>
                                <strong>To validate your participation in the FD100 winners selection, you must sign the official rules.</strong>
                                <br /><br />
                                Please follow these steps:
                                <ol>
                                    <li>Download the rules by clicking the button below</li>
                                    <li>Read the document carefully</li>
                                    <li>Sign the document (handwritten or digital signature)</li>
                                    <li>Upload the signed document via the upload button</li>
                                </ol>
                            </>
                        )}
                    </ReglementText>

                    <ReglementActions>
                        <div><strong>1.&nbsp;&nbsp;&nbsp;</strong><BlueButton onClick={() => setShowPDFPreview(!showPDFPreview)}>
                            {showPDFPreview
                                ? (i18n.language === 'fr' ? 'Masquer l\'aperçu' : 'Hide preview')
                                : (i18n.language === 'fr' ? 'Prévisualiser le règlement' : 'Preview rules')
                            }
                        </BlueButton></div>
                        
                        <div><strong>2.&nbsp;&nbsp;&nbsp;</strong><a download href={reglementPath} target="_blank" rel="noreferrer">
                            <BlueButton>
                                {i18n.language === 'fr' ? 'Télécharger le règlement' : 'Download rules'}
                            </BlueButton>
                        </a></div>

                        <div><strong>3.&nbsp;&nbsp;&nbsp;</strong>
                            {uploadingDocument ? (
                                <Spinner />
                            ) : (
                                <UploadLabel>
                                    {i18n.language === 'fr' ? 'Uploader le règlement signé' : 'Upload signed rules'}
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        onChange={handleReglementUpload}
                                    />
                                </UploadLabel>
                            )}
                        </div>
                    </ReglementActions>

                    {showPDFPreview && (
                        <PDFPreview
                            src={reglementPath}
                            title="Règlement FD100 2026"
                        />
                    )}

                    {uploadStatus && (
                        <UploadStatus className={uploadStatus.type}>
                            {uploadStatus.message}
                        </UploadStatus>
                    )}

                    {participation?.data?.confidentiality_agreement && (
                        <UploadStatus className="success">
                            <strong>{i18n.language === 'fr' ? 'Votre règlement signé est enregistré.' : 'Your signed rules are saved.'}</strong>
                            <br />
                            <DocumentLink
                                href={`${process.env.REACT_APP_MEDIA_BASE_URL}userfiles/${participation.data.confidentiality_agreement.uploaded_path}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {i18n.language === 'fr' ? 'Consulter votre règlement signé' : 'View your signed rules'}
                            </DocumentLink>
                        </UploadStatus>
                    )}
                </ReglementSection>
            </CenteredContainer>
        </Section>
    );
};

export default DocumentsASignerFD100;
